/**
 * SATBEngine.js
 * Implements "True" 4-Part Voice Leading using Cost Minimization.
 * Assigns Soprano, Alto, Tenor, Bass voices trying to minimize movement.
 */

import { CHORD_FORMULAS } from '../../data/theoryConstants.js';

const VOICES = ['S', 'A', 'T', 'B'];

const RANGES = {
    S: { min: 60, max: 81 }, // C4 - A5
    A: { min: 53, max: 74 }, // F3 - D5
    T: { min: 46, max: 67 }, // Bb2 - G4
    B: { min: 36, max: 57 }  // C2 - A3 (Lowered for Bass weight)
};

/**
 * Applies 4-part voice leading to a chord progression.
 * @param {Array} progression - Array of chord objects.
 * @param {string} strictness - 'strict', 'chorale', 'cinematic', 'loose'
 * @returns {Array} - Progression with 'satb' property added to each chord.
 */
export const applySATBVoiceLeading = (progression, strictness = 'cinematic', customRanges = null) => {
    if (!progression || progression.length === 0) return [];

    const result = [];
    let prevVoices = null;
    const ranges = customRanges || RANGES;

    progression.forEach((chord, idx) => {
        // 1. Get Pitch Classes for this chord
        const formula = getFormula(chord);
        if (chord.rootPitchClass === undefined || chord.rootPitchClass === null) return;
        const pcs = formula.map(i => (chord.rootPitchClass + i) % 12);

        if (pcs.length === 0) return;

        // 2. Determine best voicing
        let bestVoicing = null;

        if (idx === 0) {
            bestVoicing = findInitialVoicing(pcs, ranges);
        } else {
            bestVoicing = findBestConnection(prevVoices, pcs, strictness, ranges);
        }

        // 3. Store
        result.push({
            ...chord,
            notes: Object.values(bestVoicing).sort((a,b) => a-b),
            satb: bestVoicing
        });

        prevVoices = bestVoicing;
    });

    return result;
};

const getFormula = (chord) => {
    let f = CHORD_FORMULAS[chord.type] || CHORD_FORMULAS.major;
    if (chord.type === 'sus4') f = [0, 5, 7];
    return f;
};

const findInitialVoicing = (pcs, ranges) => {
    const root = pcs[0];
    const bVal = closestInRange(root, ranges.B.min, ranges.B.max);
    const tVal = closestInRange(pcs[0], ranges.T.min, ranges.T.max);
    const aVal = closestInRange(pcs[1] || pcs[0], ranges.A.min, ranges.A.max);
    const sVal = closestInRange(pcs[2] || pcs[1] || pcs[0], ranges.S.min, ranges.S.max);
    return { S: sVal, A: aVal, T: tVal, B: bVal };
};

const closestInRange = (pc, min, max) => {
    let best = -1;
    let minDist = Infinity;
    const center = (min + max) / 2;
    for (let oct = 0; oct < 10; oct++) {
        const val = pc + (oct * 12);
        if (val >= min && val <= max) {
            const dist = Math.abs(val - center);
            if (dist < minDist) {
                minDist = dist;
                best = val;
            }
        }
    }
    return best !== -1 ? best : (pc + 48);
};

/**
 * Finds the voicing that minimizes squared distance from previous voices.
 * Handles parallel fifths check based on strictness.
 */
const findBestConnection = (prev, targetPCs, strictness, ranges) => {
    let voicesToAssign = [...targetPCs];
    if (voicesToAssign.length === 3) {
        // Simple Doubling: Root preferred
        voicesToAssign.push(targetPCs[0]);
    }

    const newVoices = {};
    const isChorale = strictness === 'chorale' || strictness === 'strict';
    const isCinematic = strictness === 'cinematic';

    // 1. Bass: Find closest targetPC to prev.B
    // In Chorale style, Bass moves more (Root movements)
    newVoices.B = findClosestStrict(prev.B, targetPCs, ranges.B);

    // 2. Upper Voices (S, A, T)
    // Strategy: Minimize total motion
    newVoices.S = findClosestStrict(prev.S, targetPCs, ranges.S);
    newVoices.A = findClosestStrict(prev.A, targetPCs, ranges.A);
    newVoices.T = findClosestStrict(prev.T, targetPCs, ranges.T);

    // Fix Voice Crossings
    if (newVoices.T > newVoices.A) newVoices.T -= 12;
    if (newVoices.A > newVoices.S) newVoices.A -= 12;

    // --- CONTRARY MOTION ENFORCEMENT (Chorale & Strict Orchestral) ---
    if (isChorale) {
        // Strict contrary motion check for Bass vs Soprano
        const bassDir = newVoices.B - prev.B;
        const sopDir = newVoices.S - prev.S;

        const isParallel = (bassDir > 0 && sopDir > 0) || (bassDir < 0 && sopDir < 0);

        // If parallel, try to re-voice Soprano to move contrary
        if (isParallel && Math.abs(bassDir) > 2) { // Allow small parallel steps, penalize leaps
             // Attempt to find a Soprano note that moves contrary
             // This is a simple heuristic modification
             // In a full solver, we would use backtracking. Here, we just check alternative octaves.
             const altS = (sopDir > 0) ? newVoices.S - 12 : newVoices.S + 12;
             if (altS >= RANGES.S.min && altS <= RANGES.S.max) {
                 // Check if new interval is better?
                 newVoices.S = altS;
             }
        }
    }

    // --- PARALLEL 5TH/8VE CHECK ---
    if (isChorale) {
        let attempts = 0;
        while (checkParallelFifth(prev, newVoices) && attempts < 3) {
            // Apply Correction: Try Swapping Alto and Tenor
            const tempA = newVoices.A;
            newVoices.A = newVoices.T;
            newVoices.T = tempA;

            // Fix crossing
            if (newVoices.T > newVoices.A) newVoices.T -= 12;

            // If still parallel, try shifting Tenor contrary to bass
            if (checkParallelFifth(prev, newVoices)) {
                const bassDir = newVoices.B - prev.B;
                if (bassDir > 0) newVoices.T -= 12;
                else if (bassDir < 0) newVoices.T += 12;
            }
            attempts++;
        }
    }
    // --- CINEMATIC LOGIC ---
    else if (isCinematic) {
        // Encourage Parallel 3rds/6ths between S and A
        // If Soprano moves up, try to make Alto move up too (parallelism is good here)
        // No strict parallel 5th checks
    }

    return newVoices;
};

const checkParallelFifth = (v1, v2) => {
    // Check Bass-Tenor, Bass-Alto, Bass-Soprano
    const pairs = [['B','T'], ['B','A'], ['B','S'], ['T', 'A'], ['A', 'S']]; // Check all pairs for strict
    for (let [lower, upper] of pairs) {
        const int1 = ((v1[upper] - v1[lower]) % 12 + 12) % 12;
        const int2 = ((v2[upper] - v2[lower]) % 12 + 12) % 12;

        // Check 5ths (7) and Octaves (0)
        if ((int1 === 7 && int2 === 7) || (int1 === 0 && int2 === 0)) {
            // Check if they moved (not static oblique motion)
            if (v1[lower] !== v2[lower] && v1[upper] !== v2[upper]) {
                return true;
            }
        }
    }
    return false;
};

const findClosestStrict = (prevNote, pcs, range) => {
    let best = prevNote;
    let minDiff = Infinity;

    pcs.forEach(pc => {
        const currentOctave = Math.floor(prevNote / 12);
        [-1, 0, 1].forEach(offset => {
            const candidate = pc + ((currentOctave + offset) * 12);
            if (candidate >= range.min && candidate <= range.max) {
                const diff = Math.abs(candidate - prevNote);
                if (diff < minDiff) {
                    minDiff = diff;
                    best = candidate;
                }
            }
        });
    });
    return best;
};
